/**
 * **Le palmarès** — le classement global, et la porte vers ses propres chaînes.
 *
 * ⟦PLAN §4.11⟧ *« la run n'est jamais supprimée : elle devient un mémorial **consultable** »*.
 * Le mot qui manquait était « consultable » : côté serveur tout était là — `GET /api/me` rend
 * l'index de **toutes** les runs du compte, `GET /api/runs/:id` rend le bloc `memorial` d'une
 * run finie, `GET /api/leaderboard` rend le classement. Côté client, `me()` et `classement()`
 * étaient écrits, typés… et **sans un seul appelant**. Cet écran est la porte qui manquait.
 *
 * ## ⟦A-97⟧ La liste des chaînes est partie — et c'est un déménagement, pas une perte
 *
 * Cet écran portait DEUX listes dans un seul panneau : mes chaînes à moi, et le classement de
 * tout le monde. Une lecture intime au-dessus d'une comparaison publique — et A-97 demande un
 * écran d'historique qui en montre bien plus (durée simulée, abonnés, l'abandon). Rendre les
 * deux listes ici, puis les mêmes ailleurs, aurait fait **deux écrans qui appellent la même
 * route pour afficher la même chose**, et qui divergeraient au premier changement.
 *
 * « Tes chaînes » vit donc dans `screens/Historique/` (route `/historique`), et les deux écrans
 * se renvoient l'un à l'autre. Rien n'est devenu injoignable : l'historique est monté dans les
 * trois jeux de routes, exactement comme le palmarès.
 *
 * ## Deux choix, et ils tiennent le reste
 *
 * 1. **Un panneau, pas un écran plein.** ⟦A-7 décision 4⟧ : on ne quitte pas la chambre pour
 *    consulter quelque chose. C'est une lecture, elle se pose sur la partie en cours.
 * 2. **Le compte seulement.** Les routes sont derrière `requireUser` (SPEC-API §5.0) et le
 *    bac à sable ne parle à aucun serveur : plutôt qu'un 401 déguisé en écran vide, on dit
 *    pourquoi il n'y a rien, et le bouton n'apparaît même pas dans la barre d'actions ⟦A-5⟧.
 *
 * Aucune règle de jeu ici : cet écran ne fait que **lire** ce que le serveur a déjà décidé.
 *
 * ⟦C57 · lot 5d · A-174⟧ **Moins de texte.** Sans compte, et quand le classement est vide, l'écran
 * disait deux phrases de 20 à 35 mots : il n'en garde que la version courte (`palmares-court.ts`,
 * mesurée par un test) ; la version d'avant est derrière « 📰 Les dessous », mot pour mot.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { RunMode } from "@maxime/shared";

import { ErreurReseau } from "../../api/http";
import { classement, type EntreeClassement } from "../../api/jeu";
import { DessousBloc, LesDessous } from "../../components/LesDessous";
import { Panneau } from "../../components/Panneau";
import { modeActif } from "../../game/client";
// ⟦A-97 §2⟧ Le vocabulaire des fins vit dans `game/fins.ts` depuis que l'Historique le partage :
// « il s'appuie sur ce vocabulaire, il ne le duplique pas ». ⚠️ `libelleVictoire` répond à
// `victory_ending` (les 3 fins de victoire), `libelleFin` à `ended_reason` (5 fins d'aujourd'hui, et les 3 d'avant
// le grand ménage ⟦C60⟧, gardées pour relire les anciennes parties) — il
// existe une AUTRE constante `FINS` dans `screens/Fins/Victoire.tsx`, qui est la première.
import { libelleVictoire } from "../../game/fins";
import { nombre } from "../../game/format";

import { SANS_COMPTE_COURT, SANS_COMPTE_LONG, videCourt, videLong } from "./palmares-court";

export function Palmares() {
  const surCompte = modeActif() === "compte";
  const naviguer = useNavigate();

  const [entrees, setEntrees] = useState<EntreeClassement[] | null>(null);
  const [mode, setMode] = useState<RunMode>("hardcore");
  const [erreur, setErreur] = useState<string | null>(null);

  // Le classement se recharge quand on change de mode : normal et hardcore sont deux tableaux
  // distincts côté serveur (`where(eq(leaderboard.mode, mode))`), pas un filtre.
  useEffect(() => {
    if (!surCompte) return;
    let vivant = true;
    setEntrees(null);
    classement({ mode })
      .then((r) => {
        if (vivant) setEntrees(r.entries);
      })
      .catch((e: unknown) => {
        if (vivant) setErreur(messageErreur(e));
      });
    return () => {
      vivant = false;
    };
  }, [surCompte, mode]);

  if (!surCompte) {
    return (
      <Panneau titre="🏛 Palmarès" large>
        <p className="palmares-vide">
          {SANS_COMPTE_COURT}{" "}
          <LesDessous titre="Pourquoi un compte">
            <DessousBloc>
              <p className="dessous-texte">{SANS_COMPTE_LONG}</p>
            </DessousBloc>
          </LesDessous>
        </p>
      </Panneau>
    );
  }

  return (
    <Panneau titre="🏛 Palmarès" large>
      {erreur ? <p className="palmares-erreur">{erreur}</p> : null}

      <h2 className="bloc-titre palmares-titre">
        Classement
        <span className="palmares-modes">
          {(["hardcore", "normal"] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={`bouton discret ${mode === m ? "actif" : ""}`}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {m === "hardcore" ? "Hardcore" : "Normal"}
            </button>
          ))}
        </span>
      </h2>

      {entrees === null ? (
        <p className="palmares-vide">On récupère le classement…</p>
      ) : entrees.length === 0 ? (
        <p className="palmares-vide">
          {videCourt(mode === "hardcore" ? "hardcore" : "normal")}{" "}
          <LesDessous titre="Le classement vide">
            <DessousBloc>
              <p className="dessous-texte">{videLong(mode === "hardcore" ? "hardcore" : "normal")}</p>
            </DessousBloc>
          </LesDessous>
        </p>
      ) : (
        <ol className="classement">
          {entrees.map((e) => (
            <li className="classement-ligne" key={`${e.rank}-${e.name}-${e.created_at}`}>
              <span className="classement-rang">#{e.rank}</span>
              <span className="classement-nom">{e.name}</span>
              <span className="classement-detail">
                {nombre(e.score_total)} pts · {nombre(e.subs_final)} abonnés · J
                {nombre(e.days_simulated)}
                {e.ending ? ` · ${libelleVictoire(e.ending)}` : ""}
              </span>
            </li>
          ))}
        </ol>
      )}

      {/* ⟦A-97⟧ La porte vers ses propres chaînes. Le classement ne montre QUE les victoires
          gravées (la ligne est insérée au million, SPEC-UI §3.7) : un joueur qui n'a jamais
          gagné ne se trouve nulle part sur cette page, et c'est précisément celui à qui il faut
          dire où sont ses morts. */}
      <div className="historique-portes">
        <button type="button" className="bouton discret" onClick={() => naviguer("/historique")}>
          📜 Voir tes chaînes et leurs mémoriaux
        </button>
      </div>
    </Panneau>
  );
}

/** Le `code` est la vérité, jamais le message (`api/http.ts`) — mais ici on ne décide rien. */
function messageErreur(e: unknown): string {
  if (e instanceof ErreurReseau) return "Le serveur ne répond pas. Réessaie dans un instant.";
  return "Le classement n'a pas pu être chargé.";
}
